<template>
  <div
    v-if="updateAvailable"
    class="deployment-update-notice"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="deployment-update-title"
  >
    <div class="deployment-update-card">
      <div>
        <strong id="deployment-update-title">Eine neue Version ist verfügbar.</strong>
        <p>Bitte aktualisieren Sie die Seite, damit alle Änderungen übernommen werden.</p>
      </div>
      <div class="deployment-update-actions">
        <button type="button" class="deployment-update-later" @click="dismissUpdate">
          Später
        </button>
        <button type="button" class="deployment-update-reload" @click="reloadPage">
          Jetzt aktualisieren
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  CURRENT_DEPLOYMENT_VERSION,
  DEPLOYMENT_VERSION_CHECK_INTERVAL_MS,
  fetchDeploymentVersion,
} from '@/services/deploymentVersion';

export default {
  name: 'DeploymentUpdateNotice',
  props: {
    autoReload: {
      type: Boolean,
      default: false,
    },
    currentVersion: {
      type: String,
      default: CURRENT_DEPLOYMENT_VERSION,
    },
  },
  data() {
    return {
      updateAvailable: false,
      availableVersion: '',
      dismissedVersion: '',
      checkIntervalId: null,
      checkInFlight: false,
    };
  },
  watch: {
    autoReload(enabled) {
      if (enabled && this.updateAvailable) {
        this.reloadPage();
      }
    },
  },
  mounted() {
    this.checkForUpdate();
    this.checkIntervalId = window.setInterval(
      this.checkForUpdate,
      DEPLOYMENT_VERSION_CHECK_INTERVAL_MS
    );
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeUnmount() {
    if (this.checkIntervalId) {
      window.clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },
  methods: {
    async checkForUpdate() {
      if (this.checkInFlight) {
        return;
      }

      this.checkInFlight = true;
      try {
        const remoteVersion = await fetchDeploymentVersion();
        if (!remoteVersion || remoteVersion === this.currentVersion) {
          return;
        }

        this.availableVersion = remoteVersion;
        if (this.autoReload) {
          this.reloadPage();
          return;
        }

        if (remoteVersion !== this.dismissedVersion) {
          this.updateAvailable = true;
        }
      } catch (error) {
        console.warn('Die aktuelle Deployment-Version konnte nicht geprüft werden:', error);
      } finally {
        this.checkInFlight = false;
      }
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.checkForUpdate();
      }
    },
    dismissUpdate() {
      this.dismissedVersion = this.availableVersion;
      this.updateAvailable = false;
    },
    reloadPage() {
      const reloadUrl = new URL(window.location.href);
      reloadUrl.searchParams.set(
        '_appVersion',
        this.availableVersion || String(Date.now())
      );
      window.location.replace(reloadUrl.toString());
    },
  },
};
</script>

<style scoped>
.deployment-update-notice {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.38);
}

.deployment-update-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: min(760px, 100%);
  gap: 24px;
  padding: 20px 24px;
  border-radius: 12px;
  background: #ffffff;
  color: #334155;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.28);
  text-align: left;
}

.deployment-update-card strong {
  display: block;
  margin-bottom: 5px;
  color: #1e293b;
  font-size: 1.05rem;
}

.deployment-update-card p {
  margin: 0;
}

.deployment-update-actions {
  display: flex;
  flex-shrink: 0;
  gap: 10px;
}

.deployment-update-actions button {
  padding: 10px 15px;
  border: 1px solid #94a3b8;
  border-radius: 7px;
  cursor: pointer;
  font-weight: 700;
}

.deployment-update-later {
  background: #ffffff;
  color: #475569;
}

.deployment-update-reload {
  border-color: #475569 !important;
  background: #475569;
  color: #ffffff;
}

@media (max-width: 640px) {
  .deployment-update-notice {
    padding: 12px;
  }

  .deployment-update-card {
    align-items: stretch;
    flex-direction: column;
    gap: 16px;
  }

  .deployment-update-actions {
    justify-content: flex-end;
  }
}
</style>
